import './TierCard.css';

import { Pencil } from 'lucide-react';
import { resolveThemeToken } from '../../themeTokens';
import { benefitHeadlineFromFields } from '../../membershipBenefit';
import type { MembershipTierKpiRow, MembershipTypeRow } from '../../api/membership.api';

type Props = {
  tier: MembershipTypeRow & { stats?: MembershipTierKpiRow | null };
  index: number;
  onEdit?: (typeUuid: string) => void;
};

const NULL_SYMBOL = '—';

/** Parse the stored perks JSON into display strings (handles string[] or {title}[]). */
const parsePerks = (perksJson: string | undefined): string[] => {
  if (!perksJson) return [];
  try {
    const parsed = JSON.parse(perksJson);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((p) => (typeof p === 'string' ? p : String(p?.title ?? p?.name ?? '')))
      .filter((p) => p.trim().length > 0);
  } catch {
    return [];
  }
};

const formatPaiseAsRupees = (value: number | null | undefined): string => {
  if (value == null) return NULL_SYMBOL;
  const rupees = value / 100;
  if (rupees >= 1_00_000) {
    return `₹${(rupees / 1_00_000).toFixed(1).replace(/\.0$/, '')}L`;
  }
  if (rupees >= 1_000) {
    return `₹${(rupees / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  if (rupees === 0) return '₹0';
  return `₹${rupees.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatCount = (value: number | null | undefined): string => {
  if (value == null) return NULL_SYMBOL;
  return value.toLocaleString('en-IN');
};

const formatRetentionPercent = (renewalRate: number | null | undefined): string => {
  if (renewalRate == null) return NULL_SYMBOL;
  return `${(renewalRate * 100).toFixed(1).replace(/\.0$/, '')}%`;
};

const formatPointsMultiplier = (tier: MembershipTypeRow): string => {
  const earnRate = tier.pointsEarnRatePaisePerPoint;
  if (!earnRate || earnRate <= 0) return '1×';
  // Tier-relative multiplier vs base 100 paise/point. If a store hasn't set
  // anything explicit we just show 1×.
  const ratio = Math.round(100 / earnRate);
  if (!Number.isFinite(ratio) || ratio < 1) return '1×';
  return `${ratio}×`;
};

const TierCard = ({ tier, onEdit }: Props) => {
  const theme = resolveThemeToken(tier.themeColorToken);
  const stats = tier.stats ?? null;
  const isActive = tier.isActive !== false;

  const priceLabel = tier.pricePaise && tier.pricePaise > 0
    ? `₹${(tier.pricePaise / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
    : 'Free';
  const validityLabel = tier.validityDays && tier.validityDays > 0
    ? `${Math.round(tier.validityDays / 30)} mo`
    : 'Lifetime';
  const headline = benefitHeadlineFromFields(tier);
  const perks = parsePerks(tier.perksJson);

  return (
    <article
      className="tier-card tier-card--offering"
      data-testid={`tier-card-${tier.themeColorToken}`}
    >
      <span className="tier-card__accent-line" style={{ background: theme.cardBackground }} aria-hidden="true" />

      <header className="tier-card__header">
        <h2 className="tier-card__name" style={{ color: theme.accentHex }}>{tier.name}</h2>
        <span
          className={`tier-card__status${isActive ? ' tier-card__status--active' : ' tier-card__status--inactive'}`}
        >
          <span className="tier-card__status-dot" aria-hidden="true" />
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </header>

      {headline ? <div className="tier-card__benefit">{headline}</div> : null}

      <div className="tier-card__pricing">
        <span className="tier-card__price">{priceLabel}</span>
        <span className="tier-card__pricing-sep">·</span>
        <span className="tier-card__validity">{validityLabel}</span>
      </div>

      {perks.length > 0 ? (
        <ul className="tier-card__perks">
          {perks.map((perk, i) => (
            <li key={`${perk}-${i}`}>• {perk}</li>
          ))}
        </ul>
      ) : null}

      <dl className="tier-card__stats" aria-label="Tier statistics">
        <div className="tier-card__stat">
          <dt>MEMBERS</dt>
          <dd>{formatCount(stats?.totalMembers ?? null)}</dd>
        </div>
        <div className="tier-card__stat">
          <dt>REV · 30D</dt>
          <dd>{formatPaiseAsRupees(stats?.tierRevenue30dPaise ?? null)}</dd>
        </div>
        <div className="tier-card__stat">
          <dt>RETENTION</dt>
          <dd>{formatRetentionPercent(stats?.renewalRate30d ?? null)}</dd>
        </div>
        <div className="tier-card__stat">
          <dt>POINTS</dt>
          <dd>{formatPointsMultiplier(tier)}</dd>
        </div>
      </dl>

      <footer className="tier-card__footer">
        <button
          type="button"
          className="tier-card__icon-btn"
          aria-label="Edit tier"
          onClick={() => onEdit?.(tier.membershipTypeUuid)}
        >
          <Pencil size={14} />
        </button>
      </footer>
    </article>
  );
};

export default TierCard;
